// src/components/TeacherModifyStudentModal.jsx
import React, { useMemo, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Alert,
  Box,
  Divider,
  Grid,
  Chip,
  Collapse,
  Snackbar,
  useMediaQuery,
  AppBar,
  Toolbar,
  Stack,
  Paper,
  InputLabel,
} from "@mui/material";
import { Switch, FormControlLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { useTranslation } from "react-i18next";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  runTransaction,
  deleteField,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

import { SUBJECTS } from "../constants";
import { handlePrint } from "../components/PrintReceipt";
import { getRoleLabels, getStoredTutorType } from "../utils/roleTerminology";

const TeacherModifyStudentModal = ({
  open,
  onClose,
  student,

  // teacher scope
  teacherId, // current teacher doc id (the same stored in enrollments.teacherId / groups.teacherId)
  teacherSubjectIds = [], // subjectIds assigned to the teacher

  // data
  groups = [], // can be all groups OR already teacher-scoped groups; we still filter by teacherId safely

  // setters for local UI updates
  setStudents,
}) => {
  const { t } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const isMobile = useMediaQuery("(max-width:900px)");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [studentData, setStudentData] = useState(student ? { ...student } : null);
  const [originalStudentData, setOriginalStudentData] = useState(null);

  // Add-enrollment UI state
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showAddGroupInputs, setShowAddGroupInputs] = useState(false);

  // Devices
  const [devicesDoc, setDevicesDoc] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // ---------- Teacher-scoped options ----------
  const myGroups = useMemo(() => {
    return (groups || []).filter((g) => String(g.teacherId) === String(teacherId));
  }, [groups, teacherId]);

  const allowedSubjects = useMemo(() => {
    const allowedSet = new Set((teacherSubjectIds || []).filter(Boolean));
    return (SUBJECTS || []).filter((s) => allowedSet.has(s.id));
  }, [teacherSubjectIds]);

  // Fallback subjects derived from teacher groups when teacherSubjectIds is empty/missing.
  const groupDerivedSubjects = useMemo(() => {
    const map = new Map();
    (myGroups || []).forEach((g) => {
      if (!g?.subjectId) return;
      if (!map.has(g.subjectId)) {
        map.set(g.subjectId, {
          id: g.subjectId,
          name: g.subjectName || g.subjectId,
          level: g.level || "",
        });
      }
    });
    return Array.from(map.values());
  }, [myGroups]);

  const effectiveSubjects = useMemo(() => {
    return (allowedSubjects || []).length > 0 ? allowedSubjects : groupDerivedSubjects;
  }, [allowedSubjects, groupDerivedSubjects]);

  const uniqueLevels = useMemo(() => {
    const lvls = effectiveSubjects.map((s) => s.level).filter(Boolean);
    return [...new Set(lvls)];
  }, [effectiveSubjects]);

  const filteredSubjects = useMemo(() => {
    if (!selectedLevel) return [];
    return effectiveSubjects.filter((s) => s.level === selectedLevel);
  }, [effectiveSubjects, selectedLevel]);

  const filteredGroups = useMemo(() => {
    if (!selectedLevel) return [];
    return myGroups.filter(
      (g) =>
        g.level === selectedLevel &&
        (!selectedSubject || String(g.subjectId) === String(selectedSubject))
    );
  }, [myGroups, selectedLevel, selectedSubject]);

  // ---------- Init / reset when student changes ----------
  useEffect(() => {
    if (!student) return;

    const initialData = {
      ...student,
      disabled: student.disabled !== undefined ? student.disabled : false,
    };

    setStudentData(initialData);
    setOriginalStudentData(initialData);

    setSelectedLevel("");
    setSelectedSubject("");
    setSelectedGroup("");
    setShowAddGroupInputs(false);
    setError("");
    setSuccess("");
  }, [student]);

  // Auto-select if single level/subject (useful for coach tracks like Nutritionist -> Nutrition)
  useEffect(() => {
    if (!open) return;
    if (!selectedLevel && uniqueLevels.length === 1) {
      setSelectedLevel(uniqueLevels[0]);
    }
  }, [open, uniqueLevels, selectedLevel]);

  useEffect(() => {
    if (!open) return;
    if (selectedLevel && !selectedSubject && filteredSubjects.length === 1) {
      setSelectedSubject(filteredSubjects[0].id);
    }
  }, [open, selectedLevel, filteredSubjects, selectedSubject]);

  // Reset dependent selections
  useEffect(() => {
    setSelectedSubject("");
    setSelectedGroup("");
  }, [selectedLevel]);

  useEffect(() => {
    setSelectedGroup("");
  }, [selectedSubject]);

  // ---------- Modified check ----------
  const isModified = () => {
    if (!originalStudentData || !studentData) return false;

    const fieldsChanged =
      (studentData.firstName || "") !== (originalStudentData.firstName || "") ||
      (studentData.lastName || "") !== (originalStudentData.lastName || "") ||
      (studentData.startDate || "") !== (originalStudentData.startDate || "") ||
      (studentData.endDate || "") !== (originalStudentData.endDate || "") ||
      (studentData.disabled || false) !== (originalStudentData.disabled || false);

    const enrollmentsChanged =
      JSON.stringify(studentData.enrollments || []) !==
      JSON.stringify(originalStudentData.enrollments || []);

    return fieldsChanged || enrollmentsChanged;
  };

  // ---------- Devices ----------
  useEffect(() => {
    const uid = studentData?.uid;
    if (!open || !uid) {
      setDevicesDoc(null);
      return;
    }

    (async () => {
      try {
        setLoadingDevices(true);
        const ref = doc(db, "Devices", uid);
        const snap = await getDoc(ref);
        setDevicesDoc(snap.exists() ? snap.data() : { devices: [], models: {}, lastSeen: {} });
      } catch (e) {
        console.error("Failed to load devices:", e);
        setDevicesDoc({ devices: [], models: {}, lastSeen: {} });
      } finally {
        setLoadingDevices(false);
      }
    })();
  }, [open, studentData?.uid]);

  const formatDeviceLabel = (id) => {
    const model = devicesDoc?.models?.[id] || t("Unknown device");
    const short = id?.slice(0, 6);
    return `${model} — ${short}`;
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!studentData?.uid) return;
    const ok = window.confirm(
      role.isCoach
        ? t("Do you want to remove this device from the client's account?")
        : t("Do you want to remove this device from the student's account?")
    );
    if (!ok) return;

    try {
      const ref = doc(db, "Devices", studentData.uid);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const nextDevices = (data.devices || []).filter((d) => d !== deviceId);

        const update = { devices: nextDevices };
        update[`models.${deviceId}`] = deleteField();
        update[`lastSeen.${deviceId}`] = deleteField();

        tx.update(ref, update);
      });

      setDevicesDoc((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          devices: (prev.devices || []).filter((d) => d !== deviceId),
          models: { ...(prev.models || {}) },
          lastSeen: { ...(prev.lastSeen || {}) },
        };
        delete next.models?.[deviceId];
        delete next.lastSeen?.[deviceId];
        return next;
      });

      setSuccess(t("Device removed successfully."));
    } catch (e) {
      console.error("Failed to remove device:", e);
      setError(t("Unable to remove the device."));
    }
  };

  // ---------- Teacher-safe enrollment ops ----------
  const handleAddGroup = () => {
    if (!teacherId) {
      setError(t("Unable to identify the teacher account. Please log in again."));
      return;
    }
    if (!selectedGroup) {
      setError(t("Please select a group."));
      return;
    }

    const groupDetails = filteredGroups.find((g) => g.id === selectedGroup);
    if (!groupDetails) {
      setError(t("Invalid group."));
      return;
    }

    const isAlreadyEnrolled = (studentData.enrollments || []).some(
      (enr) => String(enr.groupId) === String(selectedGroup)
    );
    if (isAlreadyEnrolled) {
      setError(role.isCoach ? t("The client is already enrolled in this group.") : t("The student is already enrolled in this group."));
      return;
    }

    setStudentData((prev) => ({
      ...prev,
      enrollments: [
        ...(prev.enrollments || []),
        {
          groupId: groupDetails.id,
          groupName: groupDetails.name,
          subjectId: selectedSubject,
          teacherId: teacherId, // force current teacher
          entryDate: new Date().toISOString(),
        },
      ],
    }));

    setSuccess(t("Group added locally."));
    setSelectedGroup("");
    setError("");
  };

  const handleRemoveGroup = async (groupId) => {
    if (!studentData || !student) return;

    // Only allow removing enrollments that belong to THIS teacher
    const enrollmentToRemove = (studentData.enrollments || []).find(
      (enr) => String(enr.groupId) === String(groupId)
    );

    if (!enrollmentToRemove) {
      setError(t("Enrollment not found."));
      return;
    }

    if (String(enrollmentToRemove.teacherId) !== String(teacherId)) {
      setError(t("You cannot remove an enrollment that is not in your groups."));
      return;
    }

    const confirm = window.confirm(t("Are you sure you want to remove this enrollment?"));
    if (!confirm) return;

    try {
      const nextEnrollments = (studentData.enrollments || []).filter(
        (enr) => String(enr.groupId) !== String(groupId)
      );

      await updateDoc(doc(db, "students", student.id), {
        enrollments: nextEnrollments,
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        disabled: studentData.disabled,
        startDate: studentData.startDate || null,
        endDate: studentData.endDate || null,
      });

      await updateDoc(doc(db, "groups", groupId), {
        studentIds: arrayRemove(student.id),
      });

      setStudentData((prev) => ({
        ...prev,
        enrollments: nextEnrollments,
      }));

      setSuccess(t("Enrollment removed successfully."));
    } catch (e) {
      console.error("Error removing enrollment:", e);
      setError(t("An error occurred while removing the enrollment."));
    }
  };

  // Save
  const handleSave = async () => {
    if (!studentData || !student) return;

    // Safety: keep only enrollments belonging to teacher OR keep all?
    // Requirement: "teacher modifies their student" but we should not let teacher modify other-teacher enrollments.
    // Therefore we keep enrollments as-is, but ensure teacher cannot inject other teacherId in UI.
    // (Add group forces teacherId=teacherId; remove group blocks non-owned.)
    try {
      await updateDoc(doc(db, "students", student.id), {
        enrollments: studentData.enrollments || [],
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        disabled: studentData.disabled,
        startDate: studentData.startDate || null,
        endDate: studentData.endDate || null,
      });

      // Ensure groups contain studentId for all enrollments
      await Promise.all(
        (studentData.enrollments || []).map((enr) =>
          updateDoc(doc(db, "groups", enr.groupId), {
            studentIds: arrayUnion(student.id),
          })
        )
      );

      setStudents?.((prev) =>
        (prev || []).map((s) => (s.id === student.id ? { ...s, ...studentData } : s))
      );

      setSuccess(t("Changes saved successfully!"));
      onClose?.();
    } catch (e) {
      console.error("Error saving teacher edits:", e);
      setError(t("An error occurred while saving."));
    }
  };

  if (!studentData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          overflow: "hidden",
          borderRadius: isMobile ? 0 : 3,
          direction: "rtl",
          boxShadow: isMobile ? "none" : "0 20px 60px rgba(15, 23, 42, 0.18)",
          border: isMobile ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
        },
      }}
    >
      <AppBar
        position="relative"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider", direction: "rtl" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 700, textAlign: "center" }}
          >
            {role.isCoach ? t("Edit client information") : t("Edit student information")}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: "background.default",
          direction: "rtl",
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
          "& .MuiInputBase-root": { borderRadius: 2 },
          "& .MuiButton-containedPrimary": {
            borderRadius: 2,
            bgcolor: "primary.main",
            "&:hover": { bgcolor: "primary.dark" },
          },
          "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
        }}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* حالة الحساب */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }} dir="rtl">
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t("Account status")}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!studentData.disabled}
                      onChange={(e) =>
                        setStudentData({ ...studentData, disabled: !e.target.checked })
                      }
                      color="success"
                    />
                  }
                  label={studentData.disabled ? t("Activate account") : t("Disable account")}
                  sx={{ ml: 0 }}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarMonthRoundedIcon fontSize="small" />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {t("Start date")}
                      </Typography>
                    </Stack>

                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={studentData.startDate || ""}
                      onChange={(e) =>
                        setStudentData({ ...studentData, startDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={t("Select the subscription start date")}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EventAvailableRoundedIcon fontSize="small" />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {t("End date")}
                      </Typography>
                    </Stack>

                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={studentData.endDate || ""}
                      onChange={(e) =>
                        setStudentData({ ...studentData, endDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={t("Select the subscription end date")}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* أجهزة المستخدم */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {role.isCoach ? t("Client devices (max 2)") : t("Student devices (max 2)")}
                </Typography>
              </Stack>

              {!studentData?.uid ? (
                <Alert severity="warning">{role.isCoach ? t("This client has no UID.") : t("This student has no UID.")}</Alert>
              ) : loadingDevices ? (
                <Typography variant="body2">{t("Loading...")}</Typography>
              ) : (devicesDoc?.devices?.length ?? 0) === 0 ? (
                <Alert severity="info">{t("No registered devices.")}</Alert>
              ) : (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {devicesDoc.devices.map((id) => (
                    <Chip
                      key={id}
                      label={formatDeviceLabel(id)}
                      onDelete={() => handleRemoveDevice(id)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      sx={{
                        padding: "10px",
                        maxWidth: "100%",
                        whiteSpace: "normal",
                        height: "auto",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          textAlign: "right",
                        },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Student basic info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, mt: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            {role.isCoach ? t("Client details") : t("Student details")}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("First name")}
                fullWidth
                value={studentData.firstName || ""}
                onChange={(e) => setStudentData({ ...studentData, firstName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t("Last name")}
                fullWidth
                value={studentData.lastName || ""}
                onChange={(e) => setStudentData({ ...studentData, lastName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label={t("Email")} fullWidth value={studentData.email || ""} disabled />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label={t("Phone")} fullWidth value={studentData.phone || ""} disabled />
            </Grid>
          </Grid>
        </Paper>

        {/* Enrollments */}
        <Paper variant="outlined" sx={{ p: 2, direction: "rtl" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {role.enrollmentsLabel}
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddGroupInputs((prev) => !prev)}
              disabled={!teacherId}
            >
              {role.isCoach ? t("Add client to a group (program)") : t("Add student to a group (class)")}
            </Button>
          </Stack>

          {/* Show only THIS teacher's enrollments (to avoid confusion) */}
          {(() => {
            const myEnrollments = (studentData.enrollments || []).filter(
              (enr) => String(enr.teacherId) === String(teacherId)
            );

            if (myEnrollments.length === 0) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("There are no enrollments in your groups currently")}
                </Alert>
              );
            }

            return (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {myEnrollments.map((enrollment) => (
                  <Chip
                    key={enrollment.groupId}
                    label={`${t("Group")}: ${enrollment.groupName}`}
                    onDelete={() => handleRemoveGroup(enrollment.groupId)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                    sx={{
                      padding: "10px",
                      fontSize: "16px",
                      fontWeight: "500",
                      "& .MuiChip-label": { fontSize: "16px" },
                    }}
                  />
                ))}
              </Stack>
            );
          })()}

          <Divider sx={{ my: 2 }} />

          <Collapse in={showAddGroupInputs} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={uniqueLevels.length <= 1}>
                  <InputLabel id="level-label">{t("Level")}</InputLabel>
                  <Select
                    labelId="level-label"
                    label={t("Level")}
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                  >
                    <MenuItem value="">{t("Select level")}</MenuItem>
                    {uniqueLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={!selectedLevel || filteredSubjects.length === 0}>
                  <InputLabel id="subject-label">{t("Subject")}</InputLabel>
                  <Select
                    labelId="subject-label"
                    label={t("Subject")}
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                  >
                    <MenuItem value="">{t("Select subject")}</MenuItem>
                    {filteredSubjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* NOTE: Teacher is fixed (no dropdown) */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled>
                  <InputLabel id="teacher-label">{role.tutorLabel}</InputLabel>
                  <Select labelId="teacher-label" label={role.tutorLabel} value={teacherId || ""}>
                    <MenuItem value={teacherId || ""}>{role.isCoach ? t("Current coach") : t("Current teacher")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth disabled={!selectedLevel || !filteredGroups.length}>
                  <InputLabel id="group-label">{role.groupClassLabel}</InputLabel>
                  <Select
                    labelId="group-label"
                    label={role.groupClassLabel}
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    <MenuItem value="">{t("Select group")}</MenuItem>
                    {filteredGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  onClick={handleAddGroup}
                  disabled={!selectedLevel || !selectedGroup || !teacherId}
                  startIcon={<AddIcon />}
                >
                  {role.isCoach ? t("Add client to group") : t("Add student to group")}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, direction: "rtl" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button onClick={onClose} sx={{ color: "red", border: '1px solid'}}>
            {t("Cancel")}
          </Button>

          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!isModified()}
          >
            {t("Save")}
          </Button>

          <Button
            color="primary"
            onClick={() => handlePrint(studentData)}
            sx={{border: '1px solid'}}
          >
            {t("Print receipt")}
          </Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default TeacherModifyStudentModal;
